// Service pour interagir directement avec l'API OpenAI
import { 
  OpenAIConfig
} from './types';
import { apiKeyService } from './apiKeyService';
import { requestJson } from './apiClient';

/**
 * Configuration par défaut pour les appels à l'API OpenAI
 */
const defaultConfig: OpenAIConfig = {
  model: 'gpt-4o-mini',
  temperature: 0.7,
  max_tokens: 500
};

/**
 * Récupérer la clé API OpenAI depuis la base de données (cryptée)
 */
const getOpenAIApiKey = async (): Promise<string> => {
  try {
    // Essayer d'abord de récupérer depuis la base de données
    const apiKey = await apiKeyService.getApiKey('openai');
    if (apiKey) {
      return apiKey;
    }
    
    // Fallback sur les variables d'environnement
    const envApiKey = import.meta.env.VITE_OPENAI_API_KEY;
    if (envApiKey) {
      return envApiKey;
    }
    
    throw new Error('Clé API OpenAI non configurée. Veuillez la configurer dans l\'administration ou définir VITE_OPENAI_API_KEY');
  } catch (error) {
    console.error('Erreur lors de la récupération de la clé API OpenAI:', error);
    throw new Error('Impossible de récupérer la clé API OpenAI');
  }
};

/**
 * Generate recommendations using direct OpenAI API call
 */
export async function generateRecommendations(
  prompt: string,
  config: Partial<OpenAIConfig> = {}
): Promise<string> {
  try {
    
    // Merge default config with provided config
    const mergedConfig = { ...defaultConfig, ...config };
    
    // Get API key
    const apiKey = await getOpenAIApiKey();
    
    // Call OpenAI API via centralized client
    const res = await requestJson<any>('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`
      },
      body: {
        model: mergedConfig.model,
        messages: [
          { role: 'user', content: prompt }
        ],
        temperature: mergedConfig.temperature,
        max_tokens: mergedConfig.max_tokens
      },
      retry: 1,
      retryDelayMs: 400
    });
    if (!res.ok) {
      console.error('❌ Erreur de l\'API OpenAI:', res.status, res.errorText);
      throw new Error(`Erreur API OpenAI: ${res.status} - ${res.errorText || 'Erreur inconnue'}`);
    }
    const data = res.data;
    
    // Vérification de la structure de la réponse
    if (!data.choices || data.choices.length === 0) {
      console.error('❌ Réponse invalide de l\'API OpenAI:', data);
      throw new Error('Réponse invalide de l\'API OpenAI');
    }
    
    const content = data.choices[0].message?.content;
    if (!content) {
      console.error('❌ Contenu vide dans la réponse OpenAI');
      throw new Error('Contenu vide dans la réponse OpenAI');
    }
    
    return content.trim();
    
  } catch (err) {
    console.error('❌ Erreur lors de l\'appel à l\'API OpenAI:', err);
    // Fallback to generic recommendations
    return getFallbackRecommendations().join('\n\n');
  }
}

/**
 * Fallback function that provides generic recommendations
 * Used when the OpenAI API is not available
 */
function getFallbackRecommendations(industry: string = '', cmsName: string = ''): string[] {
  
  const genericRecommendations = [
    'Optimisez vos balises méta avec des mots-clés pertinents pour améliorer votre référencement.',
    'Créez un blog avec du contenu de qualité pour attirer plus de trafic qualifié.',
    'Assurez-vous que votre site est entièrement réactif pour les appareils mobiles.',
    'Améliorez la vitesse de chargement de votre site en compressant les images.',
    'Utilisez des appels à l\'action clairs et visibles pour augmenter les conversions.'
  ];
  
  if (industry) {
    genericRecommendations.unshift(
      `En tant qu'entreprise dans le secteur ${industry}, concentrez-vous sur des mots-clés spécifiques à votre industrie.`
    );
  }
  
  if (cmsName) {
    genericRecommendations.push(
      `En utilisant ${cmsName}, assurez-vous de maintenir vos plugins et thèmes à jour pour des raisons de sécurité.`
    );
  }
  
  return genericRecommendations;
}

export default {
  generateRecommendations,
  getFallbackRecommendations
};
